import { Injectable } from '@angular/core';
import { ChapterModel } from './models/chapter.model';
import { plainToClass, classToClass } from 'class-transformer';
import { uuid } from '../shared/uuid';
import { SerializeChapterService } from 'src/app/edit/serialize/chapter/serialize-chapter.service';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { HotspotModel } from './models/labels/hotspot.model';
import { BackgroundModel } from '../engine/models/background.model';
import { LabelsService } from './labels.service';
import { omit, isEmpty } from 'lodash';
import { LabelThemeEnum } from './models/labels/label-theme.enum';
import { HotspotThemesEnum } from './models/labels/hotspot.themes.enum';

declare const Human: any;

@Injectable({
  providedIn: 'root'
})
export class ChapterService {
  
  constructor(
    private serializeChapterService: SerializeChapterService,
    private labelService: LabelsService
  ) { }

  
  // Update engine with added chapter
  addChapterToEngine(chapter: ChapterModel, insertIndex: number) {
    // get chapter data from chapter model
    const serialized = this.serializeChapterService.serializeSingleChapter(chapter, true);
    const chapterData = serialized.chapter;
    
    // copy the synchronization to the chapterData before building branch
    chapterData.synchronization = JSON.parse(JSON.stringify(chapter.synchronization));
    
    // create the branch from modificed data and add to timeline
    const branch = new Human.timeline.BranchTimeline(chapterData);
    
    Human.timeline.activeRoot.addChapter(branch, {rebuild: true, position: insertIndex });
  }
  
  // Update engine to remove chapter. This does not update application state, that 
  // is handled separately in story.state.ts
  removeChapterFromEngine(chapter: ChapterModel) {
    Human.timeline.activeRoot._chapters = Human.timeline.activeRoot._chapters
      .filter(_chapter => _chapter.id !== chapter.id);

      Human.timeline.activeRoot.build();
  }

  // make a copy of the chapter model
  copyChapter(chapter: ChapterModel): ChapterModel {
    // new object and new identifications
    const chapterCopy = classToClass(chapter);
    
    // generate new id
    chapterCopy.id = uuid.idWithPrefix('chapter');

    // generate new ids for each label
    chapterCopy.labels.forEach((label) => {
      label.id = uuid.idWithPrefix('label');
      label.chapterId = chapterCopy.id;
    });

    // copy ready state
    chapterCopy.readyState = {model: true, animation: true};

    return chapterCopy;
  }
  // return the curent chapter id
  getCurrentChapterId(): string {
    return Human.timeline.activeRoot._nowBranch.id;
  }

  // replace engine chapter with chapter model
  replaceEngineChapterWithStateChapter(chapter: ChapterModel) {
    const humanChapter = Human.timeline.branchTimelines[chapter.id];
    if (!humanChapter) {
      return;
    }

    // swap stateful data
    humanChapter.synchronization = JSON.parse(JSON.stringify(chapter.synchronization));
  }

  // relay chapter synchronization data to the engine's record of the chapter
  updateChapterSynchronization(chapterId: string, synchronization: any): void {
    Human.timeline.branchTimelines[chapterId].synchronization = synchronization;
  }

  // clear color mods from synchronization on specified objects
  clearColorModsFromSync(chapterSync: any, objectIds: string[]): any {
    objectIds.forEach((id) => {
      const sync = omit(chapterSync[id] || {}, ['tintColor', 'contrast', 'brightness', 'saturation', 'opacity']);
      if (!isEmpty(sync)) {
        chapterSync[id] = sync;
      } else {
        delete chapterSync[id];
      }
    });
    return chapterSync;
  }

  // clear bool state from synchronization on specified objects
  clearBoolStateFromSync(chapterSync: any, objectIds: string[], property: string): any {
    objectIds.forEach((id) => {
      const sync = omit(chapterSync[id] || {}, [property]);
      if (!isEmpty(sync)) {
        chapterSync[id] = sync;
      } else {
        delete chapterSync[id];
      }
    });
    return chapterSync;
  }

  // create chapterModel from human branch objects
  fromHumanChapter(humanModuleData: any, storyType: string, humanChapter: any, idx: number): ChapterModel {
    // work with a copy;
    const chapterData = {...humanChapter}; 

    // copy over per chapter environment data form humanModuleData
    chapterData.environment = {...humanModuleData.chapters[idx].environment};


    // ensure every chapter has an id
    if (!chapterData.id) {
      chapterData.id = uuid.idWithPrefix('chapter');
    }

    // reformat timelinemaps
    let animated = false;
    chapterData.timelineMap = Object.values(humanChapter.maps).map((mapData: any) => {
      if (mapData.data.timeline.id !== 'dummy') {
        animated = true;
      }
      return {
        start: mapData.start,
        end: mapData.end,
        data: {
          timeline: mapData.data.timeline.id,
          start: mapData.data.start,
          end: mapData.data.end,
          fit: mapData.data.fit || false,
          onEnd: mapData.data.onEnd
        }
      };
    });
    delete chapterData.maps;

    // reformat animation -> ui
    let playCount = 0; // default to scrub
    const looping = humanChapter.animation ? humanChapter.animation.loop : false;
    if (animated && looping) {
      playCount = -1;
    } else if (animated && !looping) {
      playCount = 1;
    }
    chapterData.ui = humanChapter.ui && !isEmpty(humanChapter.ui) ? humanChapter.ui : {'play': {'count': playCount}};

    // convert annotations to labels
    
    // #TODO: Mike - Remember to make sure this doesn't break anything
    // const theme = storyType === 'module' ? LabelThemeEnum.StudioNext : LabelThemeEnum.Annotation;
    const theme = LabelThemeEnum.Annotation;

    const annotationLabels = [...chapterData.annotations || []].map((annotation) => {
      const label = this.labelService.deserializeAnnotation(annotation, chapterData, theme);
      return label;
    });

    // filter and convert hotspots to labels
    const legacyHotspots = [];
    const hotspotGroups = this.groupHotspots([...chapterData.hotspots || []].filter((hotspot) => {
      const convertable = this.labelService.filterHotspot(humanModuleData, humanChapter, hotspot);
      if (!convertable) {
        legacyHotspots.push(hotspot);
      } else {
        if (!hotspot.theme) {
          // set default theme by story type
          hotspot.theme = storyType === 'module' ? HotspotThemesEnum.studioNext : HotspotThemesEnum.classicHotspot;
        }
      }
      return convertable;
    }));

    const hotspotLabels = hotspotGroups.map((hotspotGroup) => this.labelService.deserializeHotspots(humanModuleData, humanChapter, idx, ...hotspotGroup));
    chapterData.labels = [...annotationLabels, ...hotspotLabels];
    delete chapterData.annotations;

    // reformat chapter background
    chapterData.background = BackgroundModel.deserialize(humanChapter.background);

    // return instance of ChapterModel
    const chapter = plainToClass(ChapterModel, chapterData as Object);
    chapter.hotspots = legacyHotspots;
    chapter.readyState = {model: false, animation: false};

    return chapter;
  }

  // two hotspots can be combined into one label 
  private groupHotspots(hotspots: HotspotModel[]): Array<Array<HotspotModel>> {
    const hotspotGroups: {[key: string]: any[]} = {};
    
    // helper functions to key hotspots that are reciprocal
    const keyPosition = (hotspot: HotspotModel): string => {
      if (hotspot.primitiveIndex) {
        return hotspot.primitiveIndex.toFixed(0);
      } else if (hotspot.pos) {
        return hotspot.pos.map((c) => c.toFixed(3)).join(':');
      } else {
        return Math.random().toFixed(3);
      }
      
    };

    const keyType = (type: string): string => {
      return RegExp('^circle-(plus|minus)').test(type) ? 'pair' : 'single';
    };

    // group hotspots by key
    hotspots.forEach((hotspot) => {
      const groupKey = [keyType(hotspot.type), hotspot.objectId, keyPosition(hotspot)].join('|');
      if (groupKey in hotspotGroups) {
        hotspotGroups[groupKey].push(hotspot);
      } else {
        hotspotGroups[groupKey] = [hotspot];
      }
    });

    return Object.values(hotspotGroups);
  }
}
